import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { AgentTracePanel } from './AgentTracePanel';
import { getJobByIdWithMcp, getJobTraceWithMcp } from './mcpApi';

interface JobTraceData {
  jobId: string;
  jobName: string;
  traceId: string;
  status: string;
  startedAt: string;
  completedAt?: string;
  agentSteps: any[];
}

// Document processing job types for identification
const DOCUMENT_JOBS = [
  'DocumentUploadIngestJob',
  'TextExtractionJob', 
  'MetadataExtractionJob',
  'DocumentVectorizationJob',
  'DocumentIngestJob'
];

const FILE_TYPE_ICONS: Record<string, string> = {
  'pdf': '📄',
  'doc': '📝',
  'docx': '📝',
  'txt': '📋',
  'rtf': '📝',
  'odt': '📝',
  'jpg': '🖼️',
  'jpeg': '🖼️',
  'png': '🖼️',
  'gif': '🖼️',
  'bmp': '🖼️',
  'tiff': '🖼️',
  'unknown': '📎'
};

const isDocumentJob = (jobName?: string) => {
  return jobName && DOCUMENT_JOBS.some(docJob => jobName.includes(docJob));
};

const getFileTypeIcon = (fileType?: string) => {
  if (!fileType) return FILE_TYPE_ICONS.unknown;
  return FILE_TYPE_ICONS[fileType.toLowerCase()] || FILE_TYPE_ICONS.unknown;
};

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

export default function JobTracePage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [jobData, setJobData] = useState<any>(null);
  const [traceData, setTraceData] = useState<JobTraceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { keycloak } = useKeycloak();

  useEffect(() => {
    if (!jobId || !keycloak.token) return;

    setLoading(true);
    
    // Fetch both job details and trace data
    Promise.all([
      getJobByIdWithMcp(jobId, keycloak.token).catch(err => {
        console.error('Error fetching job details:', err);
        return null;
      }),
      getJobTraceWithMcp(jobId, keycloak.token).catch(err => {
        console.error('Error fetching job trace:', err);
        return null;
      })
    ])
    .then(([jobDetails, traceDetails]) => {
      if (jobDetails) {
        setJobData(jobDetails);
      }
      if (traceDetails) {
        setTraceData(traceDetails);
      }
      setLoading(false);
    })
    .catch(err => {
      console.error('Error in job trace data fetching:', err);
      setError('Failed to load job data');
      setLoading(false);
    });
  }, [jobId, keycloak.token]);

  const isDocJob = jobData && isDocumentJob(jobData.name);

  // Extract document metadata
  const documentId = jobData?.data?._meta?.documentId || jobData?.data?.documentId;
  const fileName = jobData?.data?._meta?.fileName || jobData?.data?.fileName || jobData?.data?._meta?.filename;
  const fileType = jobData?.data?._meta?.fileType || jobData?.data?.fileType;
  const fileSize = jobData?.data?._meta?.fileSize || jobData?.data?.fileSize;

  return (
    <div className={`p-6 min-h-screen transition-colors duration-300 ${
      isDocJob 
        ? 'bg-gradient-to-br from-green-50 to-white dark:from-green-900/5 dark:to-gray-900 text-black dark:text-white' 
        : 'bg-white dark:bg-gray-900 text-black dark:text-white'
    }`}>
      {/* Enhanced Breadcrumb */}
      <div className="text-sm mb-4 text-gray-500 dark:text-gray-400">
        <Link to="/system-monitor" className="hover:underline text-lex-accent">
          System Monitor
        </Link>{' '}
        /{' '}
        <span className="font-medium text-gray-700 dark:text-gray-200">
          {isDocJob ? '📄 Document' : ''} Job Trace: {jobId}
        </span>
      </div>

      {/* Enhanced Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {isDocJob && (
            <div className="text-4xl">
              {getFileTypeIcon(fileType)}
            </div>
          )}
          <div>
            <h1 className={`text-2xl font-bold mb-2 ${
              isDocJob ? 'text-green-800 dark:text-green-200' : ''
            }`}>
              {isDocJob && <span className="mr-2">📄</span>}
              {isDocJob ? 'Document Processing' : ''} Job Trace: {jobId}
            </h1>
            {isDocJob && fileName && (
              <p className="text-green-700 dark:text-green-300 font-medium">
                {fileName}
              </p>
            )}
            {isDocJob && (
              <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                {fileType && <span>Type: {fileType.toUpperCase()}</span>}
                {fileSize && <span>Size: {formatFileSize(fileSize)}</span>}
                {jobData?.status && <span>Status: {jobData.status}</span>}
              </div>
            )}
          </div>
        </div>
        
        {/* Jump to Chat Button */}
        {isDocJob && documentId && (
          <a
            href={`/chat?documentId=${documentId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            💬 Jump to Chat
          </a>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-500">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-lex-accent"></div>
          <span>Loading job details...</span>
        </div>
      )}
      
      {error && <p className="text-red-500">Error: {error}</p>}

      {/* Document Processing Pipeline Visualization */}
      {isDocJob && jobData && (
        <div className="mb-8 p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
          <h2 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-4">📋 Document Processing Pipeline</h2>
          
          {/* Simple Pipeline Status for Single Job */}
          <div className="flex items-center gap-4 mb-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl border-2 ${
              jobData.status === 'completed'
                ? 'bg-green-500 border-green-500 text-white shadow-lg'
                : jobData.status === 'failed'
                ? 'bg-red-500 border-red-500 text-white'
                : jobData.status === 'active'
                ? 'bg-lex-accent border-lex-accent text-lex-accent-contrast animate-pulse'
                : 'bg-gray-200 border-gray-300 text-gray-500 dark:bg-gray-700 dark:border-gray-600'
            }`}>
              {jobData.status === 'failed' ? '❌' : jobData.status === 'completed' ? '✅' : '⏳'}
            </div>
            <div>
              <div className="font-semibold text-green-800 dark:text-green-200">
                {jobData.name}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {jobData.status === 'completed' ? 'Processing completed successfully' :
                 jobData.status === 'failed' ? 'Processing failed - check details below' :
                 'Processing in progress...'}
              </div>
              {jobData.attempts > 1 && (
                <div className="text-xs text-yellow-600 dark:text-yellow-400">
                  Attempt {jobData.attempts}
                </div>
              )}
            </div>
          </div>

          {/* Job Metadata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-gray-600 dark:text-gray-400">Status</div>
              <div className={`font-semibold ${
                jobData.status === 'completed' ? 'text-green-600' :
                jobData.status === 'failed' ? 'text-red-600' :
                'text-lex-accent'
              }`}>
                {jobData.status}
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">Job Type</div>
              <div className="font-semibold text-green-700 dark:text-green-300">
                Document Processing
              </div>
            </div>
            <div>
              <div className="text-gray-600 dark:text-gray-400">Attempts</div>
              <div className="font-semibold text-green-700 dark:text-green-300">
                {jobData.attempts}
              </div>
            </div>
            {documentId && (
              <div>
                <div className="text-gray-600 dark:text-gray-400">Document ID</div>
                <div className="font-mono text-xs text-green-700 dark:text-green-300">
                  {documentId.slice(0, 8)}...
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Job Details Section */}
      {jobData && (
        <div className="mb-6">
          <details className="group">
            <summary className={`cursor-pointer text-lg font-semibold mb-2 hover:text-lex-accent ${
              isDocJob ? 'text-green-800 dark:text-green-200' : ''
            }`}>
              🔍 Technical Job Details
              <span className="ml-2 text-sm text-gray-500 group-open:hidden">(click to expand)</span>
            </summary>
            <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded shadow text-sm mt-2">
              <pre className="whitespace-pre-wrap text-black dark:text-white overflow-x-auto">
                {JSON.stringify(jobData, null, 2)}
              </pre>
            </div>
          </details>
        </div>
      )}

      {/* Agent Execution Trace Section */}
      {traceData && traceData.agentSteps && traceData.agentSteps.length > 0 && (
        <div className="mt-8">
          <h2 className={`text-lg font-semibold mb-4 ${
            isDocJob ? 'text-green-800 dark:text-green-200' : ''
          }`}>
            🤖 Agent Execution Trace
          </h2>
          <div className="space-y-4">
            {traceData.agentSteps.map((step, index) => (
              <AgentTracePanel key={step.id || index} agentStep={step} />
            ))}
          </div>
        </div>
      )}

      {/* Show message if no agent steps are available */}
      {traceData && (!traceData.agentSteps || traceData.agentSteps.length === 0) && (
        <div className={`mt-8 p-4 rounded border ${
          isDocJob 
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700' 
            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
        }`}>
          <p className="text-gray-600 dark:text-gray-400 italic">
            No agent execution steps were recorded for this job. This might be a non-AI job or the agent execution logs were not captured.
          </p>
        </div>
      )}
    </div>
  );
}
